import React from 'react';
import { Shortcuts } from '@atlassian/jira-common-components-keyboard-shortcuts';
import type { ActiveKeyMap } from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-registry';
import { ff } from '@atlassian/jira-feature-flagging';
import { useViewMode, VIEW_MODE_SIDEBAR } from '@atlassian/jira-issue-context-service';
import {
	useViewSettingsActions,
	useIsFullscreen,
	View,
} from '@atlassian/jira-software-view-settings';
import { TOP_OF_CONTAINER, BOTTOM_OF_CONTAINER } from '../../../../model/constants';
import {
	ASSIGNEE_SHORTCUT_KEY,
	LABELS_SHORTCUT_KEY,
	COMMENT_SHORTCUT_KEY,
} from '../../../../model/keyboard-shortcuts/constants';
import { useBoardSelector } from '../../../../state';
import { rapidViewIdSelector } from '../../../../state/selectors/software/software-selectors';
import { useIsIncrementPlanningBoard } from '../../../../state/state-hooks/capabilities';
import type { Props } from './types';

const bentoShortcutKeys = [ASSIGNEE_SHORTCUT_KEY, LABELS_SHORTCUT_KEY, COMMENT_SHORTCUT_KEY];

export const KeyboardShortcuts = ({
	activeIssueId,
	activeIssueKey,
	activeIssueAssignee,
	currentUserAccountId,
	issueKeyForModal,
	hasSwimlanes,
	swimlaneIds,
	isCMPBoard,
	onOpenFocusedCard,
	onToggleBentoViewForFocusedCard,
	onSwimlanesToggle,
	onNextIssue,
	onPrevIssue,
	onNextColumn,
	onPrevColumn,
	onOpenAndFocusBentoField,
	onAssignToMe,
	onMoveToTopOrBottomOfContainer,
	onShowHideMenu,
}: Props) => {
	const viewMode = useViewMode();
	const isDetailViewInSidebar = viewMode === VIEW_MODE_SIDEBAR;

	const { setIsFullscreen } = useViewSettingsActions();
	const isFullscreen = useIsFullscreen();
	const view = useIsIncrementPlanningBoard() ? View.INCREMENT_PLANNING_BOARD : View.BOARD;
	const boardId = useBoardSelector(rapidViewIdSelector);

	const handleFullscreenToggle = () => {
		setIsFullscreen({ boardId: Number(boardId), isFullscreen: !isFullscreen, view });
	};

	// remove this function and put handleFullscreenToggle into keyMap when cleaning up sea-2744-jsw-tabs-nav
	const zKeyShortcut = (): ActiveKeyMap => {
		if (ff('sea-2744-jsw-tabs-nav')) {
			return {
				z: {
					callback: handleFullscreenToggle,
				},
			};
		}
		if (isCMPBoard) {
			return {
				z: {
					callback: onShowHideMenu,
				},
			};
		}
		return {};
	};

	const keyMap: ActiveKeyMap = {
		...(hasSwimlanes && {
			'-': {
				callback: () => onSwimlanesToggle(swimlaneIds),
			},
		}),
		o: {
			callback: () => onOpenFocusedCard(activeIssueKey),
		},
		t: {
			callback: () =>
				onToggleBentoViewForFocusedCard(activeIssueKey, issueKeyForModal, isDetailViewInSidebar),
		},
		j: {
			callback: onNextIssue,
		},
		k: {
			callback: onPrevIssue,
		},
		n: {
			callback: onNextColumn,
		},
		p: {
			callback: onPrevColumn,
		},
		i: {
			callback: () => onAssignToMe(activeIssueId, activeIssueAssignee, currentUserAccountId),
		},
		// when cleaning up sea-2744-jsw-tabs-nav, replace next line with `z: { callback: handleFullscreenToggle }`,
		...zKeyShortcut(),
		's t': {
			callback: () => onMoveToTopOrBottomOfContainer(TOP_OF_CONTAINER),
		},
		's b': {
			callback: () => onMoveToTopOrBottomOfContainer(BOTTOM_OF_CONTAINER),
		},
	};

	bentoShortcutKeys.forEach((shortcutKey) => {
		keyMap[shortcutKey] = {
			callback: () => {
				onOpenAndFocusBentoField(shortcutKey, activeIssueKey);
			},
		};
	});

	return <Shortcuts keyMap={keyMap} location="software.board.keyboard-shortcuts" />;
};
